import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.variantRefresh !== 'true') {
      const mainIndex = this.dataset.mainMediaIndex?.trim();

      if (mainIndex) {
        let attempts = 0;

        const showMainImage = () => {
          if (!this.isConnected) return;

          if (
            this.slideshow &&
            typeof this.slideshow.select === 'function'
          ) {
            this.slideshow.select(
              Number(mainIndex),
              undefined,
              { animate: false }
            );
          } else if (++attempts < 30) {
            requestAnimationFrame(showMainImage);
          }
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(showMainImage);
        });
      }
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    newMediaGallery.dataset.variantRefresh = 'true';
    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
